import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

// Utils
import { validateEmail } from '../../helpers/utils';

// Actions
import { gameActions } from '../../actions';

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Header from '../../components/Header';
import Reopen from '../../components/Reopen';
import List from '../../components/List';
import AddPlayer from './add_player';

import { roleNames } from '../../constants';

const statusLabels = {
  init: 'Ongoing',
  deal: 'Submitted',
  nodeal: 'Abandoned',
};

const Players = () => {
  // Hooks
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authentication.user);
  const game = useSelector((state) => state.game);
  const { id } = useParams();
  const { t } = useTranslation();
  // const player = useSelector(state => state.player);

  // const [item, setItem] = useState(game || null);
  const [pairModal, setPairModal] = useState(false);
  const [newPlayers, setNewPlayers] = useState([]);

  useEffect(() => {
    if (id) {
      dispatch(gameActions.fetchGames(id));
    }
  }, [dispatch, id]);

  // useEffect(() => {
  //   setItem(game);
  // }, [game]);

  const addPairModal = () => {
    setPairModal(!pairModal);
    setNewPlayers([]);
  };

  const handleNewPlayers = (players) => {
    setNewPlayers(players);
  };

  const submitNewPlayers = () => {
    dispatch(gameActions.addPlayers(game.data._id, newPlayers));
    addPairModal();
    setNewPlayers([]);
  };

  const { players = [], groups = [], gindex = 0 } = game.data || {};

  // Helper function:
  // If no gindex is provided, find the group the player belongs to
  // and get the index of that group, use that index+1 as gindex
  const fillGindexFromGroups = (p, groupsArray) => {
    if (typeof p.gindex === 'undefined') {
      const groupIndex = groupsArray.findIndex((g) =>
        g.players.find((gp) => gp.email === p.email)
      );
      p.gindex = groupIndex !== -1 ? groupIndex + 1 : null;
    }
    return p;
  };

  // Sort players by gindex
  const sortedPlayers = [...players].map((p) =>
    fillGindexFromGroups(p, groups)
  );

  const players_data = sortedPlayers
    ?.sort((a, b) => a.gindex - b.gindex) // Sort by gindex
    .map((p, pi) => {
      p.count = p.results.length;

      // const groupName = groups[gindex] ? groups[gindex].name : null;
      p.group = 'Group ' + p?.gindex;

      p.role = roleNames[p.rindex];
      const ml =
        process.env.REACT_APP_GAME_URL +
        '?i=' +
        p.game_id +
        '&e=' +
        p.email +
        '&t=' +
        p.token +
        '&k=1';
      p.status = groups[gindex] ? (
        <>
          {game?.round !== null && (
            <>
              {groups[gindex].status === 'init' ? (
                <span>Closed</span>
              ) : (
                <span>{statusLabels[groups[gindex].status]}</span>
              )}
            </>
          )}

          {game?.round === null && (
            <>
              <span>{statusLabels[groups[gindex].status]}</span>
              {groups[gindex].status !== 'init' && (
                <>
                  <Reopen game={game.data._id} group={groups[gindex]._id} />
                </>
              )}
            </>
          )}
        </>
      ) : (
        ''
      );
      p.magiclink =
        user.from !== 'lti-hbp' ? (
          <a href={ml} target='_blank' rel='noreferrer'>
            <code style={{ maxWidth: '100px' }}>{ml}</code>
          </a>
        ) : (
          'N/A'
        );

      // Get the next player
      const gindexColor = p?.gindex % 2 === 0 ? 'table-secondary' : '';
      p.rowClass = `${gindexColor}`;

      return p;
    });

  const validNewPlayers =
    newPlayers.length === 0
      ? false
      : newPlayers.every((pl) => validateEmail(pl.email));

  const repeatedPlayers = newPlayers.filter((pl, pli) => {
    return (
      players.find((p) => p.email === pl.email) ||
      newPlayers.find((p, pi) => p.email === pl.email && pli !== pi)
    );
  });

  return (
    <div>
      <Header />

      <h2 className='d-flex flex-column flex-md-row mt-3 sec-title fs-3'>
        <span>
          {t('Player Management')}{' '}
          <small
            className='badge badge-pill text-bg-success ms-3'
            style={{
              fontSize: '12px',
              padding: '5px 8px',
              verticalAlign: 'middle',
            }}>
            {players.length} {t('players')}
          </small>
        </span>

        {user.from !== 'lti-hbp' && (
          <small className='mt-3 mt-md-0 ms-0 ms-md-auto'>
            <button className='btn btn-outline-primary' onClick={addPairModal}>
              {t('Add Player')}
            </button>
            <button
              className='btn btn-outline-primary ms-3'
              onClick={(e) => window.location.reload()}>
              <FontAwesomeIcon icon='redo-alt' className='me-1' size='sm' />{' '}
              {t('Refresh')}
            </button>
          </small>
        )}
      </h2>

      <div className='mt-3'>
        <List
          labels={[
            t('Name'),
            t('Surname'),
            'Email',
            t('Group'),
            t('Role'),
            t('Status'),
            'MagicLink',
          ]}
          fields={[
            'name',
            'lastname',
            'email',
            'group',
            'role',
            'status',
            'magiclink',
          ]}
          data={players_data}
        />
      </div>

      <Modal
        isOpen={pairModal}
        toggle={addPairModal}
        centered={true}
        size={'lg'}>
        <ModalHeader>{t('Add Player')}</ModalHeader>
        <ModalBody className='text-center'>
          <p>
            {t('Enter the player information and click the')}{' '}
            <b>"{t('Add')}"</b> {t('button')}.
          </p>
          <AddPlayer onChange={handleNewPlayers} roles={roleNames} />
          {repeatedPlayers.length > 0 && (
            <div className='alert alert-danger mt-4'>
              {t('The following players are already in this session:')}{' '}
              <b>{repeatedPlayers.map((p) => p.email).join(', ')}</b>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className='btn btn-light' onClick={addPairModal}>
            {t('Cancel')}
          </button>
          <button
            className='btn btn-outline-primary'
            onClick={submitNewPlayers}
            disabled={!validNewPlayers || repeatedPlayers.length > 0}>
            {t('Add')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const GamePlayers = Players;
